import { useState, useEffect, useRef, Fragment, useMemo } from "react";
import { createWorker } from "tesseract.js";
import { Dialog, Transition } from "@headlessui/react";
import useDialogStore from "../stores/useDialogStore";
import DragDropUpload from "./DragDropUpload";

// Memoized regex patterns
const PATTERNS = {
  serial: /Serial\s*No\.?[:\s]*([A-Za-z0-9\-]+)/i,
  colorTotal: /Color\s+Total\s*[:|}]*\s*(\d+)/i,
  bwTotal: /B\s*&\s*W\s+Total\s*[:|}]*\s*(\d+)/i,
};

// Add these utility functions at the top of the file, after imports
const isLikelySerialNumber = (text) => {
  // Common serial number patterns
  const serialPatterns = [
    /^[A-Z0-9]{6,12}$/, // Basic alphanumeric serial
    /^[A-Z]{2,3}\d{4,8}$/, // Common format like "ABC12345"
    /^[A-Z0-9]{2,3}-[A-Z0-9]{4,8}$/, // With hyphen
    /^[A-Z0-9]{2,3}\.[A-Z0-9]{4,8}$/, // With dot
  ];
  return serialPatterns.some((pattern) => pattern.test(text));
};

const isLikelyCounter = (text) => {
  // Must be a number between 4-8 digits
  return /^\d{4,8}$/.test(text);
};

const isLikelyLabel = (text) => {
  // Common labels in printer/copier displays
  const labels = [
    "serial",
    "number",
    "counter",
    "color",
    "total",
    "copier",
    "printer",
    "scanner",
    "fax",
    "toner",
    "black",
    "white",
    "cyan",
    "magenta",
    "yellow",
    "maintenance",
    "status",
    "error",
    "warning",
    "ready",
  ];
  return labels.some((label) => text.toLowerCase().includes(label));
};

function AutoOcr({
  onSerialNumber,
  onCounterColor,
  onCounterBlack,
  onImageCapture = () => {},
  brand,
  onDeviceImages,
}) {
  // Combine form field states into a single object
  const [formData, setFormData] = useState({
    serialNumber: "",
    counterColor: "",
    counterBlack: "",
    activeField: "serial_no",
  });
  const { closeDialog } = useDialogStore();

  // Combine OCR-related states
  const [ocrState, setOcrState] = useState({
    extractedText: "",
    capturedImage: null,
    words: [],
    showResults: false,
    isProcessing: false,
    error: null,
  });

  // Keep only essential UI states
  const [isMobile, setIsMobile] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const workerRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const [uploadMode, setUploadMode] = useState("camera"); // 'camera' or 'upload'
  const uploadInputRef = useRef(null);

  // Add new state for categorized words
  const [categorizedWords, setCategorizedWords] = useState({
    serialNumbers: [],
    counters: [],
    labels: [],
    other: [],
  });

  // Update form field handler
  const handleFieldChange = (field, value) => {
    // Get the input element
    const inputId = {
      serialNumber: "ocr_serial-no",
      counterColor: "ocr_counter-color",
      counterBlack: "ocr_counter-black",
    }[field];

    const inputElement = document.getElementById(inputId);
    // If the value is coming from an input event, use the input's value
    const finalValue =
      inputElement?.value !== undefined ? inputElement.value : value;

    setFormData((prev) => ({ ...prev, [field]: finalValue }));

    // Call appropriate callback
    switch (field) {
      case "serialNumber":
        onSerialNumber(finalValue);
        break;
      case "counterColor":
        onCounterColor(finalValue);
        break;
      case "counterBlack":
        onCounterBlack(finalValue);
        break;
    }
  };

  // Update active field handler
  const handleFieldFocus = (field) => {
    setFormData((prev) => ({ ...prev, activeField: field }));
  };

  // Reset OCR state handler
  const resetOcrState = () => {
    setOcrState((prev) => ({
      ...prev,
      capturedImage: null,
      showResults: false,
      words: [],
      error: null,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Detect if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Setup video stream for desktop
  useEffect(() => {
    if (
      !isMobile &&
      uploadMode === "camera" &&
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia
    ) {
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };

      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.addEventListener("loadedmetadata", () => {
              videoRef.current.play();
            });
          }
        })
        .catch((error) => {
          console.error("Error accessing camera:", error);
        });

      return () => {
        const stream = videoRef.current?.srcObject;
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
      };
    } else {
      // Stop any existing stream when switching away from camera mode
      const stream = videoRef.current?.srcObject;
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      }
    }
  }, [isMobile, facingMode, uploadMode]);

  const toggleCamera = () => {
    setFacingMode(facingMode === "user" ? "environment" : "user");
  };

  const restartCamera = () => {
    if (videoRef.current) {
      const stream = videoRef.current.srcObject;
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      videoRef.current.srcObject = null;
    }

    // Force a re-render by updating facingMode once
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  const handleCaptureImage = () => {
    if (isMobile) {
      fileInputRef.current?.click();
    } else {
      const video = videoRef.current;
      if (video && !video.paused) {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/png");
        processImage(dataUrl);
      }
    }
  };

  const handleFileSelect = async (event) => {
    console.log("handleFileSelect called");
    const file = event.target.files[0];
    if (file) {
      console.log("File selected:", file.name, file.size);
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log("File read successfully");
        processImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileUpload = async (file) => {
    console.log("handleFileUpload called with file:", file?.name);
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      console.log("File uploaded and read successfully");
      processImage(e.target.result);
    };
    reader.onerror = () => {
      console.log("File read error");
      setOcrState((prev) => ({
        ...prev,
        error: "Failed to read the image file",
      }));
    };
    reader.readAsDataURL(file);
  };

  // Initialize worker once
  useEffect(() => {
    const initWorker = async () => {
      try {
        const worker = await createWorker();
        await worker.loadLanguage("eng");
        await worker.initialize("eng");
        workerRef.current = worker;
      } catch (err) {
        console.error("Failed to initialize OCR worker:", err);
        setOcrState((prev) => ({
          ...prev,
          error: "Failed to initialize OCR. Please refresh the page.",
        }));
      }
    };
    initWorker();

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, []);

  // Function to process the image data using OCR
  const processImage = async (dataUrl) => {
    console.log(
      "processImage called with dataUrl:",
      dataUrl ? "valid" : "invalid"
    );

    // Check if the OCR worker is ready
    if (!workerRef.current) {
      console.log("OCR worker not ready");
      setOcrState((prev) => ({
        ...prev,
        error: "OCR is not ready. Please try again.",
      }));
      return;
    }

    // Validate the image data
    if (!dataUrl) {
      setOcrState((prev) => ({
        ...prev,
        error: "Invalid image data. Please upload a clear image.",
      }));
      return;
    }

    // Update OCR state to indicate processing has started
    setOcrState((prev) => ({
      ...prev,
      isProcessing: true,
      error: null,
      capturedImage: dataUrl,
    }));
    onImageCapture(dataUrl);

    try {
      // Perform OCR recognition on the image
      const {
        data: { text: extracted },
      } = await workerRef.current.recognize(dataUrl);

      console.log("Raw extracted text:", extracted);

      // Split the extracted text into words and clean them
      const words = extracted
        .split(/\s+/)
        .filter((word) => word.length > 0)
        .map((word) => word.replace(/[.,;:!?]/g, ""))
        .filter((word) => word.length > 0);

      console.log("All cleaned words:", words);

      // Categorize the words based on predefined patterns
      const categorized = words.reduce(
        (acc, word) => {
          if (isLikelySerialNumber(word)) acc.serialNumbers.push(word);
          else if (isLikelyCounter(word)) acc.counters.push(word);
          else if (isLikelyLabel(word)) acc.labels.push(word);
          else if (word.length > 2) acc.other.push(word);
          return acc;
        },
        {
          serialNumbers: [],
          counters: [],
          labels: [],
          other: [],
        }
      );

      // Update state with categorized words and extracted text
      setCategorizedWords(categorized);
      setOcrState((prev) => ({
        ...prev,
        extractedText: extracted,
        words: words,
        showResults: true,
        isProcessing: false,
      }));

      console.log("Categorized words:", categorized);

      // Extract specific data using regex patterns
      const serial = extracted.match(PATTERNS.serial)?.[1];
      const color = extracted.match(PATTERNS.colorTotal)?.[1];
      const black = extracted.match(PATTERNS.bwTotal)?.[1];

      // Function to update form fields with extracted values
      const updateField = (field, value, inputId) => {
        if (value) {
          const inputElement = document.getElementById(inputId);
          if (inputElement) {
            inputElement.value = value;
            const event = new Event("input", { bubbles: true });
            inputElement.dispatchEvent(event);
          }
          handleFieldChange(field, value);
        }
      };

      // Update form fields with extracted data
      updateField("serialNumber", serial, "ocr_serial-no");
      updateField("counterColor", color, "ocr_counter-color");
      updateField("counterBlack", black, "ocr_counter-black");

      // Fallback logic for updating fields if no pattern match
      const allNumericValues = words
        .filter((w) => /^\d{4,6}$/.test(w))
        .map(Number);

      const sorted = [...allNumericValues].sort((a, b) => b - a);

      if (!serial && categorized.serialNumbers.length > 0) {
        updateField(
          "serialNumber",
          categorized.serialNumbers[0],
          "ocr_serial-no"
        );
      }

      if (!color && sorted.length > 0) {
        updateField("counterColor", sorted[0], "ocr_counter-color");
      }

      if (!black && sorted.length > 1) {
        updateField("counterBlack", sorted[1], "ocr_counter-black");
      }
    } catch (err) {
      // Handle errors during OCR processing
      console.error("OCR Error:", err);
      setOcrState((prev) => ({
        ...prev,
        error:
          "Failed to process image. Please try again with a clearer or different photo.",
        isProcessing: false,
      }));
    }
  };

  const handleWordSelect = (word) => {
    const fieldMap = {
      serial_no: "serialNumber",
      counter_color: "counterColor",
      counter_black: "counterBlack",
    };
    const field = fieldMap[formData.activeField];
    if (field) {
      handleFieldChange(field, word);
    }
  };

  // Add a component for displaying categorized words
  const CategorizedWords = ({ words, title, onSelect, className = "" }) => {
    if (!words.length) return null;

    return (
      <div className={`space-y-2 ${className}`}>
        <h5 className="text-sm font-medium text-gray-700">{title}</h5>
        <div className="flex flex-wrap gap-2">
          {words.map((word, index) => (
            <button
              key={index}
              onClick={() => {
                // Get the active input element
                const activeField = formData.activeField;
                const inputId = {
                  serial_no: "ocr_serial-no",
                  counter_color: "ocr_counter-color",
                  counter_black: "ocr_counter-black",
                }[activeField];

                const inputElement = document.getElementById(inputId);
                if (inputElement) {
                  // Replace the value directly in the input
                  inputElement.value = word;
                  // Trigger change event to update state
                  const event = new Event("input", { bubbles: true });
                  inputElement.dispatchEvent(event);
                }
                onSelect(word);
              }}
              className="px-3 py-1 bg-white border rounded-md hover:bg-blue-50 transition-colors text-sm"
            >
              {word}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Add new handler for done button
  const handleDone = () => {
    // Get values from input elements
    const serialInput = document.getElementById("ocr_serial-no");
    const colorInput = document.getElementById("ocr_counter-color");
    const blackInput = document.getElementById("ocr_counter-black");

    // Get values from inputs and update props
    const serialValue = serialInput?.value || formData.serialNumber;
    const colorValue = colorInput?.value || formData.counterColor;
    const blackValue = blackInput?.value || formData.counterBlack;

    // Update all values one final time before closing
    onSerialNumber(serialValue);
    onCounterColor(colorValue);
    onCounterBlack(blackValue);
    closeDialog();
  };

  // Add clear field handler
  const handleClearField = (field) => {
    const inputId = {
      serialNumber: "ocr_serial-no",
      counterColor: "ocr_counter-color",
      counterBlack: "ocr_counter-black",
    }[field];

    // Clear the input element
    const inputElement = document.getElementById(inputId);
    if (inputElement) {
      inputElement.value = "";
    }

    // Update form state and props
    handleFieldChange(field, "");
  };

  return (
    <main className="space-y-6">
      <h3 className="text-2xl font-semibold text-center text-gray-800">
        Camera OCR
      </h3>

      {ocrState.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {ocrState.error}
        </div>
      )}

      {ocrState.isProcessing && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Processing image...</span>
        </div>
      )}

      {/* Mode Selection */}
      <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
        <button
          onClick={() => {
            setUploadMode("camera");
            if (!isMobile) {
              restartCamera();
            }
          }}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            uploadMode === "camera"
              ? "bg-white shadow text-blue-600"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          📸 Camera
        </button>
        <button
          onClick={() => setUploadMode("upload")}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            uploadMode === "upload"
              ? "bg-white shadow text-blue-600"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          📁 Upload
        </button>
      </div>

      {uploadMode === "camera" ? (
        isMobile ? (
          // Mobile camera view
          <div className="flex flex-col items-center gap-4">
            <input
              type="file"
              capture="environment"
              accept="image/*"
              x-webkit-airplay="allow"
              x-webkit-capture="allow"
              x-webkit-live-text="allow"
              onChange={handleFileSelect}
              ref={fileInputRef}
              className="hidden"
            />
            <div className="relative w-full">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full bg-blue-600 text-white rounded-md py-3 font-semibold text-lg flex items-center justify-center gap-2"
              >
                <span>📸 Open Camera</span>
              </button>
            </div>
          </div>
        ) : (
          // Desktop camera view
          <>
            <div className="space-y-4">
              <div className="aspect-video bg-black rounded-md overflow-hidden h-[50vh] sm:h-auto">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  playsInline
                  muted
                />
              </div>
            </div>
            {/* Camera controls separated visually */}
            <div className="flex gap-4 mt-6 mb-8 p-4 bg-gray-50 border border-gray-200 rounded-lg shadow-sm justify-center">
              <button
                onClick={handleCaptureImage}
                className="flex-1 bg-blue-600 text-white rounded-md py-2 font-semibold"
              >
                📸 Capture
              </button>
              <button
                onClick={toggleCamera}
                className="flex-1 bg-gray-200 text-gray-800 rounded-md py-2 font-semibold"
              >
                🔄 Toggle Camera
              </button>
            </div>
          </>
        )
      ) : (
        // Upload mode
        <DragDropUpload
          onFileSelect={handleFileUpload}
          ref={uploadInputRef}
          className="space-y-4"
        >
          <div className="flex flex-col items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-12 h-12 text-gray-400"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
              />
            </svg>
            <div className="text-gray-600">
              <span className="font-medium text-blue-600">Click to upload</span>{" "}
              or drag and drop
            </div>
            <p className="text-sm text-gray-500">PNG, JPG, GIF up to 5MB</p>
          </div>
        </DragDropUpload>
      )}

      {ocrState.capturedImage && (
        <div className="border rounded-md w-full relative">
          <img
            src={ocrState.capturedImage}
            alt="Captured"
            className="w-full object-contain"
            x-webkit-live-text="allow"
            x-webkit-airplay="allow"
            x-webkit-capture="allow"
          />
        </div>
      )}

      {ocrState.showResults && (
        <div className="border sm:block  rounded-md p-4 bg-gray-50 space-y-4">
          <h4 className="font-medium mb-2">Detected Text (Tap to select):</h4>

          {(() => {
            const fieldMap = {
              serial_no: "serialNumber",
              counter_color: "counterColor",
              counter_black: "counterBlack",
            };
            const activeKey = fieldMap[formData.activeField];
            return (
              <>
                <CategorizedWords
                  words={categorizedWords.serialNumbers}
                  title="Possible Serial Numbers"
                  onSelect={(word) => handleFieldChange(activeKey, word)}
                  className="bg-blue-50/50 p-3 rounded-md"
                />
                <CategorizedWords
                  words={categorizedWords.counters}
                  title="Possible Counter Values"
                  onSelect={(word) => handleFieldChange(activeKey, word)}
                  className="bg-green-50/50 p-3 rounded-md"
                />
              </>
            );
          })()}
        </div>
      )}

      <div className="text-box space-y-4">
        <div className="relative">
          <label
            htmlFor="ocr_serial-no"
            className="block text-sm font-medium text-gray-900"
          >
            Serial Number
          </label>
          <div className="relative">
            <input
              value={formData.serialNumber}
              onChange={(e) =>
                handleFieldChange("serialNumber", e.target.value)
              }
              type="text"
              name="ocr_serial-no"
              id="ocr_serial-no"
              className="mt-1 block w-full rounded-lg border p-2 border-gray-300 h-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm pr-8"
              onClick={() => handleFieldFocus("serial_no")}
            />
            {formData.serialNumber && (
              <button
                onClick={() => handleClearField("serialNumber")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                type="button"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="relative">
          <label
            htmlFor="ocr_counter-color"
            className="block text-sm font-medium text-gray-900"
          >
            Counter Color
          </label>
          <div className="relative">
            <input
              value={formData.counterColor}
              onChange={(e) =>
                handleFieldChange("counterColor", e.target.value)
              }
              type="text"
              name="ocr_counter-color"
              id="ocr_counter-color"
              className="mt-1 block w-full rounded-lg border p-2 border-gray-300 h-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm pr-8"
              onClick={() => handleFieldFocus("counter_color")}
            />
            {formData.counterColor && (
              <button
                onClick={() => handleClearField("counterColor")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                type="button"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="relative">
          <label
            htmlFor="ocr_counter-black"
            className="block text-sm font-medium text-gray-900"
          >
            Counter Black
          </label>
          <div className="relative">
            <input
              value={formData.counterBlack}
              onChange={(e) =>
                handleFieldChange("counterBlack", e.target.value)
              }
              type="text"
              name="ocr_counter-black"
              id="ocr_counter-black"
              className="mt-1 block w-full rounded-lg border p-2 border-gray-300 h-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm pr-8"
              onClick={() => handleFieldFocus("counter_black")}
            />
            {formData.counterBlack && (
              <button
                onClick={() => handleClearField("counterBlack")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                type="button"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-4 pt-4">
        <button
          onClick={resetOcrState}
          disabled={ocrState.isProcessing}
          className="flex-1 border border-blue-600 text-blue-600 rounded-md py-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          🔁 Retake
        </button>
        <button
          onClick={handleDone}
          disabled={ocrState.isProcessing}
          className="flex-1 bg-blue-600 text-white rounded-md py-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ✅ Done
        </button>
      </div>
    </main>
  );
}

export default AutoOcr;
